/**
 * TEX Notification Manager · v2.7.65+E88
 * ═══════════════════════════════════════════════════════════════════
 * One-stop admin console (App Management → TEX NOTIFICATION MANAGER)
 * that unifies every surface of the multi-channel notification stack:
 *
 *   • Header · masked Twilio + VAPID config summary
 *   • Master kill-switch toggle (hard-mute every outbound)
 *   • Test broadcast panel (SMS · Voice · Email · single target)
 *   • Driver notification path registry — who has Web Push vs SMS-only
 *     vs no channel; per-driver quick Ping button
 *   • Ping history · chronological audit log of every dispatch → driver
 *     ping attempt, with the channels that landed
 *   • Recent voice-call outbox (last 20)
 *   • Recent SMS outbox for notification kinds (last 20)
 *
 * All actions are surfaced with clear success/failure toasts. The auto-
 * refresh cadence is intentionally low (60s) so heavy admins don't
 * hammer the API just by leaving the tab open.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Bell, ShieldOff, Send, Phone, Mail, MessageSquare, Zap, Wifi,
  History, ClipboardList, Loader2, RefreshCw, AlertTriangle,
  CheckCircle2, BarChart3, Megaphone, Timer,
} from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";

const REFRESH_MS = 60_000;

function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const s = Math.floor(ms / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmtChannels(row) {
  const bits = [];
  if (row.socket_open)     bits.push("ws");
  if (row.web_push_sent)   bits.push(`push:${row.web_push_sent}`);
  if (row.sms_sent)        bits.push("sms");
  if (!bits.length)        bits.push(row.channels?.length ? row.channels.join(",") : "—");
  return bits.join(" + ");
}


// ═══ Section 1 · Config summary + Kill switch ══════════════════════════
function ConfigCard({ summary, onReload }) {
  const [killPending, setKillPending] = useState(false);
  const [durationMin, setDurationMin] = useState(0);  // 0 = indefinite
  const enabled = summary?.kill_switch?.enabled;
  const autoUnarmAt = summary?.kill_switch?.auto_unarm_at;
  const twOk = summary?.twilio?.configured;
  const vapOk = summary?.vapid?.private_set;

  // Countdown timer for auto-unarm
  const [remaining, setRemaining] = useState("");
  useEffect(() => {
    if (!enabled || !autoUnarmAt) { setRemaining(""); return undefined; }
    const tick = () => {
      const ms = Date.parse(autoUnarmAt) - Date.now();
      if (ms <= 0) { setRemaining("expiring…"); onReload(); return; }
      const mins = Math.floor(ms / 60000);
      const secs = Math.floor((ms % 60000) / 1000);
      setRemaining(`${mins}m ${String(secs).padStart(2, "0")}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [enabled, autoUnarmAt, onReload]);

  const toggleKill = async (nextEnabled) => {
    setKillPending(true);
    try {
      const body = { enabled: nextEnabled };
      if (nextEnabled && durationMin > 0) body.duration_minutes = durationMin;
      const res = await adminApi.post("/admin/notify/kill-switch", body);
      if (res.data.enabled) {
        const dur = durationMin > 0 ? ` for ${durationMin}min` : "";
        toast.warning(`Kill switch ARMED${dur} — all outbound muted`);
      } else {
        toast.success("Kill switch OFF — outbound resumed");
      }
      onReload();
    } catch (e) {
      toast.error(`Kill switch failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setKillPending(false);
    }
  };

  return (
    <Card className="bg-zinc-900 border-amber-500/40 p-5 space-y-4" data-testid="tex-config-card">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-black tracking-wide flex items-center gap-2">
          <Send className="h-5 w-5 text-amber-400"/>TEX · Configuration Summary
        </h2>
        <Button size="sm" variant="outline" onClick={onReload}
                data-testid="tex-refresh-summary"
                className="bg-zinc-950 border-zinc-700 text-zinc-300 text-[10px] h-7">
          <RefreshCw className="h-3 w-3 mr-1"/>Refresh
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
          <div className="text-zinc-500 uppercase text-[10px] font-mono mb-1">Twilio SMS + Voice</div>
          <div className={`font-black ${twOk ? "text-emerald-300" : "text-rose-300"}`}>
            {twOk ? "CONFIGURED" : "NOT CONFIGURED"}
          </div>
          <div className="text-zinc-500 font-mono mt-1">SID {summary?.twilio?.account_sid || "—"}</div>
          <div className="text-zinc-500 font-mono">From SMS {summary?.twilio?.from_sms || "—"}</div>
          <div className="text-zinc-500 font-mono">From Voice {summary?.twilio?.from_voice || "—"}</div>
        </div>
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
          <div className="text-zinc-500 uppercase text-[10px] font-mono mb-1">Web Push (VAPID)</div>
          <div className={`font-black ${vapOk ? "text-emerald-300" : "text-rose-300"}`}>
            {vapOk ? "CONFIGURED" : "NOT CONFIGURED"}
          </div>
          <div className="text-zinc-500 font-mono mt-1">Contact {summary?.vapid?.contact || "—"}</div>
          <div className="text-zinc-500 font-mono">Key {summary?.vapid?.public_key || "—"}</div>
        </div>
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
          <div className="text-zinc-500 uppercase text-[10px] font-mono mb-1">Push Subscriptions</div>
          <div className="font-black text-sky-300 text-lg">{summary?.push_subscriptions?.total || 0}</div>
          <div className="text-zinc-500 font-mono">{summary?.push_subscriptions?.usable || 0} usable</div>
        </div>
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
          <div className="text-zinc-500 uppercase text-[10px] font-mono mb-1">Outbox Totals</div>
          <div className="text-zinc-500 font-mono">SMS <span className="text-zinc-300 font-black">{summary?.sms_outbox?.delivered || 0}</span>/{summary?.sms_outbox?.total || 0}</div>
          <div className="text-zinc-500 font-mono">Voice <span className="text-zinc-300 font-black">{summary?.voice_outbox?.delivered || 0}</span>/{summary?.voice_outbox?.total || 0}</div>
          <div className="text-zinc-500 font-mono">Pings {summary?.ping_requests_total || 0}</div>
        </div>
      </div>
      <div className={`rounded-lg border-2 p-3 ${enabled ? "border-rose-500/60 bg-rose-500/10" : "border-emerald-500/40 bg-emerald-500/5"}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <ShieldOff className={`h-6 w-6 ${enabled ? "text-rose-300" : "text-zinc-500"}`}/>
          <div className="flex-1 min-w-0">
            <div className="font-black text-sm flex items-center gap-2">
              Master Kill Switch — {enabled ? "ARMED" : "OFF"}
              {enabled && remaining && (
                <Badge data-testid="tex-kill-remaining" className="bg-rose-500/15 border-rose-500/40 text-rose-300 font-mono text-[10px] flex items-center gap-1">
                  <Timer className="h-3 w-3"/>{remaining}
                </Badge>
              )}
            </div>
            <div className="text-[11px] text-zinc-500">
              {enabled
                ? (autoUnarmAt
                    ? "Every outbound is muted; auto-un-arms at the countdown above."
                    : "Every outbound is muted indefinitely. Flip off when done.")
                : "Notifications flow normally. Set a silence duration below before arming."}
            </div>
          </div>
          <Switch
            data-testid="tex-kill-switch-toggle"
            checked={!!enabled}
            disabled={killPending}
            onCheckedChange={toggleKill}
          />
        </div>
        {!enabled && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="tex-kill-duration-picker">
            <span className="text-[10px] uppercase text-zinc-500 font-mono">Silence duration</span>
            {[
              { m: 0,   label: "Indefinite" },
              { m: 5,   label: "5 min" },
              { m: 15,  label: "15 min" },
              { m: 30,  label: "30 min" },
              { m: 60,  label: "1 hour" },
              { m: 120, label: "2 hours" },
            ].map(({ m, label }) => (
              <button
                key={m}
                data-testid={`tex-kill-dur-${m}`}
                onClick={() => setDurationMin(m)}
                className={`px-2 py-1 rounded text-[10px] font-mono border ${durationMin === m
                    ? "bg-amber-500/20 border-amber-500/60 text-amber-200"
                    : "bg-zinc-950 border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}


// ═══ Section 2 · Test broadcast ════════════════════════════════════════
// v2.7.65+E236 · Hoisted to module scope. Previously defined INSIDE
// TestBroadcastCard, which made it a brand-new component type on every
// keystroke → React remounted the <Input> and the field lost focus after a
// single character. Defining it once at module scope keeps the input mounted.
function TestBroadcastRow({ Icon, label, kind, val, setVal, hint, busy, fire }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 space-y-2" data-testid={`tex-test-${kind}`}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase text-zinc-300">
        <Icon className="h-4 w-4 text-amber-400"/>{label}
      </div>
      <Input
        data-testid={`tex-test-${kind}-input`}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={hint}
        className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm h-8"
      />
      <Button
        data-testid={`tex-test-${kind}-btn`}
        size="sm"
        onClick={() => fire(kind, val, setVal)}
        disabled={busy === kind || !val}
        className="w-full bg-amber-400 hover:bg-amber-300 text-zinc-950 font-black text-[11px] h-7"
      >
        {busy === kind
          ? <><Loader2 className="h-3 w-3 mr-1 animate-spin"/>Sending…</>
          : <>Send test {label.split(" ")[0]}</>}
      </Button>
    </div>
  );
}

function TestBroadcastCard() {
  const [smsTo,  setSmsTo]  = useState("");
  const [voiceTo, setVoiceTo] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [busy, setBusy]     = useState(null);
  const fire = async (channel, to, setTo) => {
    if (!to || to.length < 3) { toast.error("Enter a destination first"); return; }
    setBusy(channel);
    try {
      const { data } = await adminApi.post("/admin/notify/test", { channel, to });
      if (data.ok) toast.success(`Test ${channel} sent — check the destination in a moment`);
      else         toast.error(`Test ${channel} failed: ${data?.result?.error || "unknown"}`);
      if (data.ok) setTo("");
    } catch (e) {
      toast.error(`Test ${channel} failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };
  return (
    <Card className="bg-zinc-900 border-sky-500/30 p-5 space-y-4" data-testid="tex-test-card">
      <div>
        <h2 className="text-base font-black tracking-wide flex items-center gap-2">
          <Zap className="h-5 w-5 text-sky-400"/>TEX · Test Broadcast (Dry Run)
        </h2>
        <p className="text-[11px] text-zinc-500 mt-1">
          Send a single test message on each channel. Bypasses the kill switch — this IS the diagnostic path.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <TestBroadcastRow Icon={MessageSquare} label="Test SMS"   kind="sms"
             val={smsTo}   setVal={setSmsTo}   hint="+15551234567" busy={busy} fire={fire}/>
        <TestBroadcastRow Icon={Phone}         label="Test Voice Call" kind="voice"
             val={voiceTo} setVal={setVoiceTo} hint="+15551234567" busy={busy} fire={fire}/>
        <TestBroadcastRow Icon={Mail}          label="Test Email" kind="email"
             val={emailTo} setVal={setEmailTo} hint="you@example.com" busy={busy} fire={fire}/>
      </div>
    </Card>
  );
}


// ═══ Section 3 · Driver notification path registry ═════════════════════
function DriverRegistryCard({ summary }) {
  const [rows, setRows]   = useState([]);
  const [count, setCount] = useState({ has_push: 0, sms_only: 0, no_channel: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // v2.7.65+E89 · Batch broadcast dialog state
  const [bcOpen, setBcOpen] = useState(false);
  const [bcFilter, setBcFilter] = useState("sms_only");
  const [bcMessage, setBcMessage] = useState("");
  const [bcBusy, setBcBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/notify/push-subscriptions",
                                          { params: { include_offline: true } });
      setRows(data.drivers || []);
      setCount(data.counters || {});
    } catch (e) {
      toast.error(`Registry load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ping = async (d) => {
    setBusyId(d.driver_id);
    try {
      const { data } = await adminApi.post(`/admin/drivers/${d.driver_id}/ping`);
      const chans = [];
      if (data.socket_open)   chans.push("WS");
      if (data.web_push_sent) chans.push(`Push × ${data.web_push_sent}`);
      if (data.sms_sent)      chans.push("SMS");
      toast.success(`Pinged ${d.name} · ${chans.join(" + ") || "no channel"}`);
    } catch (e) {
      toast.error(`Ping failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const fireBroadcast = async () => {
    if (!bcMessage.trim() || bcMessage.trim().length < 3) {
      toast.error("Message is too short"); return;
    }
    setBcBusy(true);
    try {
      const { data } = await adminApi.post("/admin/notify/broadcast-drivers",
                                             { message: bcMessage.trim(), filter: bcFilter });
      toast.success(`Broadcast fired · ${data.delivered}/${data.matches} delivered`);
      setBcOpen(false);
      setBcMessage("");
    } catch (e) {
      toast.error(`Broadcast failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBcBusy(false);
    }
  };

  const filterCounts = useMemo(() => ({
    sms_only:   count.sms_only || 0,
    has_push:   count.has_push || 0,
    no_channel: count.no_channel || 0,
    all_active: (count.sms_only || 0) + (count.has_push || 0),
  }), [count]);

  const badge = (row) => {
    if (row.primary_channel === "web_push") return <Badge className="bg-emerald-500/15 border-emerald-500/40 text-emerald-300 text-[9px]">WEB PUSH</Badge>;
    if (row.primary_channel === "sms_only") return <Badge className="bg-amber-500/15 border-amber-500/40 text-amber-300 text-[9px]">SMS ONLY</Badge>;
    return <Badge className="bg-rose-500/15 border-rose-500/40 text-rose-300 text-[9px]">NO CHANNEL</Badge>;
  };

  return (
    <Card className="bg-zinc-900 border-emerald-500/30 p-5 space-y-4" data-testid="tex-registry-card">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-black tracking-wide flex items-center gap-2">
            <Wifi className="h-5 w-5 text-emerald-400"/>Driver Notification Path Registry
          </h2>
          <p className="text-[11px] text-zinc-500 mt-1">
            Every driver plus the highest-quality channel we currently have to reach them.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-emerald-500/15 border-emerald-500/40 text-emerald-300 font-mono">PUSH {count.has_push || 0}</Badge>
          <Badge className="bg-amber-500/15 border-amber-500/40 text-amber-300 font-mono">SMS {count.sms_only || 0}</Badge>
          <Badge className="bg-rose-500/15 border-rose-500/40 text-rose-300 font-mono">NONE {count.no_channel || 0}</Badge>
          <Button
            data-testid="tex-batch-broadcast-open"
            size="sm"
            onClick={() => setBcOpen(true)}
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-black text-[10px] h-7"
          >
            <Megaphone className="h-3 w-3 mr-1"/>Batch Broadcast
          </Button>
          <Button size="sm" variant="outline" onClick={load}
                  data-testid="tex-registry-refresh"
                  className="bg-zinc-950 border-zinc-700 text-zinc-300 text-[10px] h-7">
            <RefreshCw className="h-3 w-3 mr-1"/>Reload
          </Button>
        </div>
      </div>
      {loading && <div className="text-[11px] text-zinc-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin"/>Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-[11px] text-zinc-500">No drivers configured yet.</div>
      )}
      <div className="rounded-lg border border-zinc-800 overflow-hidden">
        <table className="w-full text-[11px]">
          <thead className="bg-zinc-950 text-zinc-500 uppercase text-[10px] font-mono">
            <tr>
              <th className="px-3 py-2 text-left">Driver</th>
              <th className="px-3 py-2 text-left">Online</th>
              <th className="px-3 py-2 text-left">Channel</th>
              <th className="px-3 py-2 text-left">Phone</th>
              <th className="px-3 py-2 text-left">Browsers</th>
              <th className="px-3 py-2 text-left">Last Sub</th>
              <th className="px-3 py-2 text-right"/>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.driver_id} data-testid={`tex-registry-row-${d.driver_id}`} className="border-t border-zinc-800/60 hover:bg-zinc-950/60">
                <td className="px-3 py-2 font-bold text-zinc-100">{d.name}</td>
                <td className="px-3 py-2">
                  {d.is_online ? <span className="text-emerald-300">● online</span> : <span className="text-zinc-600">○ offline</span>}
                </td>
                <td className="px-3 py-2">{badge(d)}</td>
                <td className="px-3 py-2 font-mono text-zinc-400">{d.phone || <span className="text-rose-400">—</span>}</td>
                <td className="px-3 py-2 font-mono text-zinc-500">{(d.browsers || []).join(", ") || "—"}</td>
                <td className="px-3 py-2 text-zinc-500">{timeAgo(d.last_sub_updated)}</td>
                <td className="px-3 py-2 text-right">
                  <Button
                    data-testid={`tex-registry-ping-${d.driver_id}`}
                    size="sm"
                    variant="outline"
                    disabled={busyId === d.driver_id}
                    onClick={() => ping(d)}
                    className="bg-zinc-950 border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-[10px] h-7"
                  >
                    {busyId === d.driver_id
                      ? <Loader2 className="h-3 w-3 animate-spin"/>
                      : <>Ping</>}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Batch broadcast composer */}
      <Dialog open={bcOpen} onOpenChange={setBcOpen}>
        <DialogContent className="bg-zinc-950 border-amber-500/40 text-zinc-100 max-w-lg" data-testid="tex-batch-broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-300">
              <Megaphone className="h-5 w-5"/>Batch Broadcast SMS
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase text-zinc-500 font-mono mb-2 block">Target group</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "sms_only",   label: "SMS-only drivers",   hint: "No push subscription; phones only." },
                  { key: "has_push",   label: "Push-capable drivers", hint: "At least one active Web Push sub." },
                  { key: "all_active", label: "All active drivers", hint: "Every driver with a phone number." },
                  { key: "no_channel", label: "No channel (audit)", hint: "No phone AND no push — logs only." },
                ].map(({ key, label, hint }) => (
                  <button
                    key={key}
                    data-testid={`tex-bc-filter-${key}`}
                    onClick={() => setBcFilter(key)}
                    className={`text-left rounded-lg border p-2 ${bcFilter === key
                        ? "border-amber-500/60 bg-amber-500/10"
                        : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"}`}
                  >
                    <div className="text-xs font-bold text-zinc-100 flex items-center justify-between">
                      {label}
                      <span className="text-[10px] font-mono text-zinc-500">{filterCounts[key] || 0}</span>
                    </div>
                    <div className="text-[10px] text-zinc-500">{hint}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-zinc-500 font-mono mb-2 block">Message (max 280 chars)</Label>
              <textarea
                data-testid="tex-bc-message"
                value={bcMessage}
                onChange={(e) => setBcMessage(e.target.value.slice(0, 280))}
                placeholder='e.g. "Push is temporarily down — call dispatch at 405-506-8920 for the next 30 min."'
                className="w-full h-24 rounded bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm p-2 font-mono"
              />
              <div className="text-[10px] text-zinc-600 mt-1">{bcMessage.length}/280</div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBcOpen(false)}
                    className="bg-zinc-900 border-zinc-800 text-zinc-300">Cancel</Button>
            <Button
              data-testid="tex-bc-send"
              disabled={bcBusy || !bcMessage.trim()}
              onClick={fireBroadcast}
              className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-black"
            >
              {bcBusy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Sending…</> : `Send to ${filterCounts[bcFilter] || 0} drivers`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ═══ Section 3b · Batch Broadcast History (v2.7.65+E90) ════════════════
function BroadcastHistoryCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/notify/broadcast-history",
                                          { params: { limit: 25 }});
      setRows(data.items || []);
    } catch (e) {
      toast.error(`Broadcast history load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filterLabel = (f) => ({
    sms_only:   "SMS-only",
    has_push:   "Push-capable",
    all_active: "All active",
    no_channel: "No channel",
  }[f] || f);

  return (
    <Card className="bg-zinc-900 border-amber-500/25 p-5 space-y-4" data-testid="tex-broadcast-history-card">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-black tracking-wide flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-amber-400"/>Batch Broadcast History
          </h2>
          <p className="text-[11px] text-zinc-500 mt-1">
            Every bulk SMS fired via the Batch Broadcast dialog. Audit trail for ops.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load}
                data-testid="tex-broadcast-history-refresh"
                className="bg-zinc-950 border-zinc-700 text-zinc-300 text-[10px] h-7">
          <RefreshCw className="h-3 w-3 mr-1"/>Reload
        </Button>
      </div>
      {loading && <div className="text-[11px] text-zinc-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin"/>Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-[11px] text-zinc-500">No batch broadcasts yet. Fire one from the Registry card above.</div>
      )}
      {!loading && rows.length > 0 && (
        <div className="rounded-lg border border-zinc-800 overflow-hidden">
          <table className="w-full text-[11px]">
            <thead className="bg-zinc-950 text-zinc-500 uppercase text-[10px] font-mono">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">By</th>
                <th className="px-3 py-2 text-left">Filter</th>
                <th className="px-3 py-2 text-right">Matched</th>
                <th className="px-3 py-2 text-right">Delivered</th>
                <th className="px-3 py-2 text-right">Failed</th>
                <th className="px-3 py-2 text-left">Message</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`tex-broadcast-row-${r.id}`} className="border-t border-zinc-800/60">
                  <td className="px-3 py-2 text-zinc-400 font-mono whitespace-nowrap">{timeAgo(r.created_at)}</td>
                  <td className="px-3 py-2 text-zinc-300">{r.created_by || "—"}</td>
                  <td className="px-3 py-2">
                    <Badge className="bg-amber-500/10 border-amber-500/40 text-amber-300 text-[9px]">
                      {filterLabel(r.filter_mode)}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-200">{r.matches}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-300 font-black">{r.delivered}</td>
                  <td className="px-3 py-2 text-right font-mono text-rose-400">{r.failed}</td>
                  <td className="px-3 py-2 text-zinc-400 italic max-w-[300px] truncate" title={r.message}>{r.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


// ═══ Section 4 · Ping history audit log ════════════════════════════════
function PingHistoryCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // v2.7.65+E89 · filters
  const [drivers, setDrivers]   = useState([]);
  const [driverId, setDriverId] = useState("");
  const [outcome, setOutcome]   = useState("");
  const [search, setSearch]     = useState("");

  const loadDrivers = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/notify/push-subscriptions",
                                          { params: { include_offline: true }});
      setDrivers((data.drivers || []).map((d) => ({ id: d.driver_id, name: d.name })));
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadDrivers(); }, [loadDrivers]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 60 };
      if (driverId) params.driver_id = driverId;
      if (outcome)  params.outcome = outcome;
      const { data } = await adminApi.get("/admin/notify/ping-history", { params });
      setRows(data.items || []);
    } catch (e) {
      toast.error(`Ping history load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [driverId, outcome]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) =>
      (r.driver_name || "").toLowerCase().includes(q)
      || (r.requested_by || "").toLowerCase().includes(q)
      || (r.driver_id || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <Card className="bg-zinc-900 border-violet-500/30 p-5 space-y-4" data-testid="tex-ping-history-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-black tracking-wide flex items-center gap-2">
            <History className="h-5 w-5 text-violet-400"/>Ping Request Audit Log
          </h2>
          <p className="text-[11px] text-zinc-500 mt-1">
            Chronological trail of every dispatch → driver ping and which channels landed.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load}
                data-testid="tex-ping-history-refresh"
                className="bg-zinc-950 border-zinc-700 text-zinc-300 text-[10px] h-7">
          <RefreshCw className="h-3 w-3 mr-1"/>Reload
        </Button>
      </div>
      {/* v2.7.65+E89 · Filters row */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="tex-ping-filters">
        <select
          data-testid="tex-ping-filter-driver"
          value={driverId}
          onChange={(e) => setDriverId(e.target.value)}
          className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[11px] text-zinc-200"
        >
          <option value="">All drivers</option>
          {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select
          data-testid="tex-ping-filter-outcome"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[11px] text-zinc-200"
        >
          <option value="">All outcomes</option>
          <option value="delivered">Delivered (any channel)</option>
          <option value="failed">Failed (no channel)</option>
          <option value="ws">Via WS socket</option>
          <option value="web_push">Via Web Push</option>
          <option value="sms">Via SMS fallback</option>
        </select>
        <Input
          data-testid="tex-ping-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search driver name / dispatcher…"
          className="bg-zinc-950 border-zinc-800 text-zinc-100 text-[11px] h-7 w-56"
        />
        {(driverId || outcome || search) && (
          <button
            data-testid="tex-ping-filters-clear"
            onClick={() => { setDriverId(""); setOutcome(""); setSearch(""); }}
            className="text-[10px] text-zinc-500 hover:text-zinc-200 underline"
          >
            Clear
          </button>
        )}
        <span className="text-[10px] text-zinc-600 font-mono ml-auto">{filtered.length} shown</span>
      </div>
      {loading && <div className="text-[11px] text-zinc-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin"/>Loading…</div>}
      {!loading && filtered.length === 0 && <div className="text-[11px] text-zinc-500">No ping requests match the current filters.</div>}
      <div className="rounded-lg border border-zinc-800 overflow-hidden">
        <table className="w-full text-[11px]">
          <thead className="bg-zinc-950 text-zinc-500 uppercase text-[10px] font-mono">
            <tr>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-left">Driver</th>
              <th className="px-3 py-2 text-left">By</th>
              <th className="px-3 py-2 text-left">Channels landed</th>
              <th className="px-3 py-2 text-left">Was Online</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`tex-ping-row-${r.id}`} className="border-t border-zinc-800/60">
                <td className="px-3 py-2 text-zinc-400 font-mono">{timeAgo(r.requested_at)}</td>
                <td className="px-3 py-2 font-bold text-zinc-100">{r.driver_name || r.driver_id.slice(0,8)}</td>
                <td className="px-3 py-2 text-zinc-400">{r.requested_by || "—"}</td>
                <td className="px-3 py-2 font-mono">
                  {r.socket_open || r.web_push_sent || r.sms_sent ? (
                    <span className="text-emerald-300">{fmtChannels(r)}</span>
                  ) : (
                    <span className="text-rose-400">none</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {r.was_online
                    ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 inline"/>
                    : <AlertTriangle className="h-3.5 w-3.5 text-amber-400 inline"/>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


// ═══ Section 4b · Push Delivery Rate Sparkline (v2.7.65+E89) ═══════════
function DeliveryTimelineCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/notify/delivery-timeline", { params: { hours }});
      setData(data);
    } catch (e) {
      toast.error(`Timeline load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [hours]);
  useEffect(() => { load(); }, [load]);

  // Compute stacked-bar SVG geometry.
  const chart = useMemo(() => {
    if (!data?.buckets) return null;
    const buckets = data.buckets;
    const W = 720; const H = 140;
    const paddingL = 30; const paddingR = 10; const paddingT = 6; const paddingB = 22;
    const innerW = W - paddingL - paddingR;
    const innerH = H - paddingT - paddingB;
    const barW = Math.max(2, (innerW / buckets.length) - 2);
    const maxTotal = Math.max(1, ...buckets.map((b) => b.total));
    const scale = innerH / maxTotal;
    const bars = buckets.map((b, i) => {
      const x = paddingL + i * (innerW / buckets.length) + 1;
      const wsH   = b.ws * scale;
      const pushH = b.web_push * scale;
      const smsH  = b.sms * scale;
      const noneH = b.none * scale;
      const bottom = H - paddingB;
      let y = bottom;
      const segments = [];
      // Stack order (bottom → top): ws (emerald), push (sky), sms (amber), none (rose)
      [
        ["ws",       wsH,   "#34d399"],
        ["web_push", pushH, "#38bdf8"],
        ["sms",      smsH,  "#fbbf24"],
        ["none",     noneH, "#f43f5e"],
      ].forEach(([k, h, color]) => {
        if (h > 0) {
          y -= h;
          segments.push({ x, y, w: barW, h, color, key: `${i}-${k}` });
        }
      });
      return { x, hour: b.hour_label, segments, total: b.total };
    });
    // Axis ticks — 4 ticks
    const ticks = [0, Math.ceil(maxTotal * 0.33), Math.ceil(maxTotal * 0.66), maxTotal];
    return { W, H, paddingL, paddingB, bars, ticks, scale, buckets, innerW };
  }, [data]);

  return (
    <Card className="bg-zinc-900 border-sky-500/30 p-5 space-y-3" data-testid="tex-delivery-timeline-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-black tracking-wide flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-sky-400"/>Push Delivery Rate · Last {hours}h
          </h2>
          <p className="text-[11px] text-zinc-500 mt-1">
            Stacked hourly breakdown of ping-request channel usage. Green = WS · Blue = Push · Amber = SMS · Red = failed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[6, 24, 72, 168].map((h) => (
            <button
              key={h}
              data-testid={`tex-timeline-h-${h}`}
              onClick={() => setHours(h)}
              className={`px-2 py-1 rounded text-[10px] font-mono ${hours === h
                  ? "bg-sky-500/20 border border-sky-500/60 text-sky-200"
                  : "bg-zinc-950 border border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}
            >
              {h < 24 ? `${h}h` : `${h / 24}d`}
            </button>
          ))}
          <Button size="sm" variant="outline" onClick={load}
                  data-testid="tex-timeline-refresh"
                  className="bg-zinc-950 border-zinc-700 text-zinc-300 text-[10px] h-7">
            <RefreshCw className="h-3 w-3 mr-1"/>Reload
          </Button>
        </div>
      </div>
      {/* Totals row */}
      {data?.totals && (
        <div className="flex items-center gap-3 text-[11px] font-mono">
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 bg-emerald-400 rounded-sm"/>WS <span className="text-zinc-200 font-black">{data.totals.ws}</span></span>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 bg-sky-400 rounded-sm"/>Push <span className="text-zinc-200 font-black">{data.totals.web_push}</span></span>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 bg-amber-400 rounded-sm"/>SMS <span className="text-zinc-200 font-black">{data.totals.sms}</span></span>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 bg-rose-500 rounded-sm"/>None <span className="text-zinc-200 font-black">{data.totals.none}</span></span>
          <span className="text-zinc-500 ml-auto">Total pings: <span className="text-zinc-200 font-black">{data.totals.total}</span></span>
        </div>
      )}
      {loading && <div className="text-[11px] text-zinc-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin"/>Loading…</div>}
      {!loading && chart && (
        <div className="w-full overflow-x-auto">
          <svg viewBox={`0 0 ${chart.W} ${chart.H}`} className="w-full h-[160px]" data-testid="tex-timeline-svg">
            {/* Y-axis ticks + gridlines */}
            {chart.ticks.map((v, i) => {
              const y = chart.H - chart.paddingB - v * chart.scale;
              return (
                <g key={i}>
                  <line x1={chart.paddingL} x2={chart.W - 10} y1={y} y2={y} stroke="#27272a" strokeDasharray="2 4"/>
                  <text x={chart.paddingL - 4} y={y + 3} textAnchor="end" className="fill-zinc-600 text-[9px] font-mono">{v}</text>
                </g>
              );
            })}
            {/* Bars */}
            {chart.bars.map((b) => (
              <g key={b.x}>
                {b.segments.map((s) => (
                  <rect key={s.key} x={s.x} y={s.y} width={s.w} height={s.h} fill={s.color} opacity="0.9">
                    <title>{b.hour} — total {b.total}</title>
                  </rect>
                ))}
              </g>
            ))}
            {/* X-axis: label every 6th hour */}
            {chart.bars.map((b, i) => (
              (i % Math.max(1, Math.floor(chart.bars.length / 8)) === 0) ? (
                <text key={`t-${i}`} x={b.x + 2} y={chart.H - 8} className="fill-zinc-600 text-[9px] font-mono">
                  {b.hour}
                </text>
              ) : null
            ))}
          </svg>
        </div>
      )}
    </Card>
  );
}


// ═══ Section 5 · Recent SMS + Voice outbox ═════════════════════════════
function OutboxCard() {
  const [voice, setVoice] = useState([]);
  const [sms, setSms]     = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, s] = await Promise.all([
        adminApi.get("/admin/voice/outbox", { params: { limit: 15 }}),
        adminApi.get("/admin/sms/outbox",   { params: { limit: 15 }}),
      ]);
      setVoice(v.data.items || []);
      setSms(s.data.items || []);
    } catch (e) {
      toast.error(`Outbox load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const Section = ({ Icon, title, kind, items, color }) => (
    <div className={`rounded-lg border border-${color}-500/30 bg-zinc-950 p-3`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs font-black uppercase">
          <Icon className={`h-4 w-4 text-${color}-400`}/>{title}
        </div>
        <span className="text-[9px] text-zinc-600 font-mono">last {items.length}</span>
      </div>
      <div className="space-y-1 max-h-[280px] overflow-y-auto">
        {items.length === 0 && <div className="text-[10px] text-zinc-600 py-2">No records.</div>}
        {items.map((it) => (
          <div key={it.id} data-testid={`tex-${kind}-row-${it.id}`}
               className="text-[10px] flex items-center gap-2 border-b border-zinc-900/50 py-1">
            <span className={`h-2 w-2 rounded-full ${it.delivered ? "bg-emerald-400" : "bg-rose-400"}`}/>
            <span className="text-zinc-400 font-mono w-16">{timeAgo(it.created_at)}</span>
            <span className="text-zinc-200 font-mono w-28 truncate">{it.to}</span>
            <span className="text-zinc-500 truncate flex-1">
              {it.kind ? `[${it.kind}]` : ""} {it.error ? <span className="text-rose-400">· {it.error}</span> : (it.body || "").slice(0, 50)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4" data-testid="tex-outbox-card">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-black tracking-wide flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-zinc-400"/>Recent Outbox Activity
        </h2>
        <Button size="sm" variant="outline" onClick={load}
                data-testid="tex-outbox-refresh"
                className="bg-zinc-950 border-zinc-700 text-zinc-300 text-[10px] h-7">
          <RefreshCw className="h-3 w-3 mr-1"/>Reload
        </Button>
      </div>
      {loading && <div className="text-[11px] text-zinc-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin"/>Loading…</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section Icon={Phone}          title="Voice Call Outbox" kind="voice" items={voice} color="rose"/>
        <Section Icon={MessageSquare}  title="SMS Outbox"        kind="sms"   items={sms}   color="sky"/>
      </div>
    </Card>
  );
}


// ═══ v2.7.65+E91 · Corporate Email Aliases Card ═══════════════════════
function CorporateAliasesCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testEvent, setTestEvent] = useState("help_request");
  const [testNote, setTestNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/notify/email-aliases");
      setData(data);
    } catch (e) {
      toast.error(`Alias load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runTest = async () => {
    if (!testEvent) return;
    setTesting(true);
    try {
      const { data } = await adminApi.post("/admin/notify/email-alias-test", {
        event_type: testEvent, note: testNote || "",
      });
      if (data?.ok) {
        toast.success(`Test alias email sent to ${(data.recipients || []).join(", ")}`);
      } else {
        toast.warning("Alias routing OK — but Resend returned no message-id (kill-switch or fallback active?)");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Alias test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="tex-corporate-aliases">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-emerald-300"/>
          <div className="text-[12px] uppercase tracking-widest text-emerald-300 font-bold">Corporate Email Aliases</div>
        </div>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="tex-aliases-reload"
                className="h-7 text-xs text-zinc-400">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`}/>Reload
        </Button>
      </div>
      <p className="text-[11px] text-zinc-400 mb-3">
        Every platform event routes to the correct corporate mailbox. Update <code className="text-amber-300">ALIAS_FOR_EVENT</code> in
        <code className="text-amber-300"> backend/services/email_service.py</code> to add new events.
      </p>
      {loading && !data && (
        <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin"/>Loading aliases…</div>
      )}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4" data-testid="tex-aliases-grid">
            {Object.entries(data.aliases || {}).map(([key, addr]) => (
              <div key={key} className="bg-zinc-950 border border-zinc-800 rounded p-2"
                   data-testid={`tex-alias-${key}`}>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{key}</div>
                <div className="text-xs text-emerald-300 font-mono truncate">{addr}</div>
              </div>
            ))}
          </div>

          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Event → Recipients</div>
          <div className="max-h-64 overflow-y-auto border border-zinc-800 rounded" data-testid="tex-aliases-events">
            <table className="w-full text-xs">
              <thead className="bg-zinc-950 text-zinc-400 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-2 py-1.5">Event Type</th>
                  <th className="text-left px-2 py-1.5">Recipients</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.events || {}).map(([evt, cfg]) => (
                  <tr key={evt} className="border-t border-zinc-800" data-testid={`tex-alias-event-${evt}`}>
                    <td className="px-2 py-1.5 font-mono text-zinc-300">{evt}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {(cfg.recipients || []).map((r) => (
                          <Badge key={r} variant="outline" className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 text-[10px]">
                            {r}
                          </Badge>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 pt-3 border-t border-zinc-800">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Dry-Run Alias Test</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <select
                data-testid="tex-alias-test-event"
                value={testEvent}
                onChange={(e) => setTestEvent(e.target.value)}
                className="bg-zinc-950 border border-zinc-700 rounded h-9 px-2 text-xs text-zinc-100"
              >
                {Object.keys(data.events || {}).map((evt) => (
                  <option key={evt} value={evt}>{evt}</option>
                ))}
              </select>
              <Input
                data-testid="tex-alias-test-note"
                value={testNote}
                onChange={(e) => setTestNote(e.target.value)}
                placeholder="Optional note (shows in email body)"
                className="bg-zinc-950 border-zinc-700 h-9 text-xs md:col-span-1"
              />
              <Button
                data-testid="tex-alias-test-send"
                onClick={runTest}
                disabled={testing}
                className="h-9 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold text-xs"
              >
                {testing ? <Loader2 className="h-3 w-3 animate-spin"/> : <><Send className="h-3 w-3 mr-1"/>Send Test</>}
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}


// ═══ Root ══════════════════════════════════════════════════════════════
export default function TexNotificationManagerTab() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/notify/config-summary");
      setSummary(data);
    } catch (e) {
      toast.error(`Config summary load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="space-y-4 pb-16" data-testid="tex-notification-manager">
      <Card className="bg-gradient-to-r from-amber-500/10 via-zinc-900 to-zinc-900 border-amber-500/30 p-5">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10">
            <Bell className="h-6 w-6 text-amber-300"/>
          </div>
          <div>
            <h1 className="text-lg font-black tracking-widest text-amber-300" data-testid="tex-title">TEX NOTIFICATION MANAGER</h1>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              Unified control surface for the multi-channel notification pipeline.
              SMS · Voice · Email · Web Push · Kill Switch · Test Harness · Audit.
            </p>
          </div>
        </div>
      </Card>
      {loading && !summary && (
        <div className="text-[12px] text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin"/>Loading TEX console…
        </div>
      )}
      {summary && (
        <>
          <ConfigCard summary={summary} onReload={load}/>
          <TestBroadcastCard/>
          <DriverRegistryCard summary={summary}/>
          <BroadcastHistoryCard/>
          <DeliveryTimelineCard/>
          <PingHistoryCard/>
          <CorporateAliasesCard/>
          <OutboxCard/>
        </>
      )}
    </div>
  );
}
